import { useState, useEffect, useRef } from 'react';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  orderBy,
  updateDoc,
  doc,
  deleteDoc
} from 'firebase/firestore';
import AuthForm from './Auth';

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'long', day: 'numeric'
  });
}

// Spaced Repetition Algorithm
function sm2Algorithm(card, quality) {
  let { interval, repetition, efactor } = card;
  const now = new Date();
  if (quality < 3) {
    repetition = 0;
    interval = 1;
  } else {
    if (repetition === 0) {
      interval = 1;
    } else if (repetition === 1) {
      interval = 6;
    } else {
      interval = Math.round(interval * efactor);
    }
    repetition += 1;
    efactor = Math.max(1.3, efactor + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02)));
  }
  return {
    ...card,
    interval,
    repetition,
    efactor,
    dueDate: new Date(now.setDate(now.getDate() + interval)).toISOString()
  };
}

export default function App() {
  const [user, setUser] = useState(null);
  const [entries, setEntries] = useState([]);
  const [entry, setEntry] = useState('');
  const [category, setCategory] = useState('intellectual');
  const [hook, setHook] = useState('');
  const [flashcards, setFlashcards] = useState([]);
  const [reviewPrompt, setReviewPrompt] = useState(null);
  const [search, setSearch] = useState('');
  const [entriesTab, setEntriesTab] = useState('all');
  const [showSection, setShowSection] = useState('journal');
  const [starNext, setStarNext] = useState(false);
  const textareaRef = useRef();

  // Authentication
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, currentUser => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Load Entries (always from Firestore)
  const loadEntries = async (uid) => {
    try {
      const q = query(
        collection(db, 'entries'),
        where('uid', '==', uid),
        orderBy('date', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const loaded = [];
      querySnapshot.forEach(docSnap => {
        loaded.push({ ...docSnap.data(), id: docSnap.id });
      });
      setEntries(loaded);
    } catch (err) {
      alert('Failed to load entries: ' + err.message);
    }
  };

  useEffect(() => {
    if (user) loadEntries(user.uid);
    else setEntries([]);
  }, [user]);

  // Load Flashcards
  const loadFlashcards = async (uid) => {
    try {
      const q = query(
        collection(db, 'flashcards'),
        where('uid', '==', uid)
      );
      const querySnapshot = await getDocs(q);
      const loaded = [];
      querySnapshot.forEach(docSnap => {
        loaded.push({ ...docSnap.data(), id: docSnap.id });
      });
      setFlashcards(loaded);
    } catch (err) {
      alert('Failed to load flashcards: ' + err.message);
    }
  };

  useEffect(() => {
    if (user) loadFlashcards(user.uid);
    else {
      setFlashcards([]);
      setReviewPrompt(null);
    }
  }, [user]);

  // Find the next due flashcard for review
  useEffect(() => {
    const now = new Date();
    const dueCard = flashcards.find(card => new Date(card.dueDate) <= now);
    setReviewPrompt(dueCard || null);
  }, [flashcards]);

  // Wisdom Bank: group hooks from entries, and collect flashcards that are not from entries
  const wisdomEntries = entries
    .filter(e => e.hook && e.id)
    .map(e => ({
      type: 'entry',
      id: e.id,
      date: formatDate(e.date),
      hook: e.hook
    }));

  const entryHookSet = new Set(entries.filter(e => e.hook).map(e => e.hook));
  const flashcardWisdom = flashcards
    .filter(fc => fc.prompt && !entryHookSet.has(fc.prompt) && fc.id)
    .map(fc => ({
      type: 'flashcard',
      id: fc.id,
      date: formatDate(fc.dueDate || fc.createdAt || new Date()),
      hook: fc.prompt
    }));

  // Combine and group by date
  const allWisdom = [...wisdomEntries, ...flashcardWisdom];
  const groupByDate = arr =>
    arr.reduce((acc, e) => {
      const dateKey = e.date;
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(e);
      return acc;
    }, {});

  const groupedEntries = groupByDate(
    entries
      .filter(e =>
        e.id &&
        (entriesTab === 'all' || e.starred) &&
        (e.text?.toLowerCase().includes(search.toLowerCase()) ||
          e.hook?.toLowerCase().includes(search.toLowerCase()))
      )
      .map(e => ({ ...e, date: formatDate(e.date) }))
  );
  const groupedWisdom = groupByDate(allWisdom);

  // Star/unstar entry (updates Firestore)
  const toggleStar = async entry => {
    if (!entry.id) return;
    try {
      const ref = doc(db, 'entries', entry.id);
      await updateDoc(ref, { starred: !entry.starred });
      await loadEntries(user.uid);
    } catch (err) {
      alert('Failed to update star: ' + err.message);
    }
  };

  // Delete entry (updates Firestore and UI)
  const handleDeleteEntry = async (entryId) => {
    if (!entryId) {
      alert('Cannot delete: Entry has no ID.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await deleteDoc(doc(db, 'entries', entryId));
      await loadEntries(user.uid);
    } catch (err) {
      alert('Failed to delete entry: ' + err.message);
    }
  };

  // Delete flashcard (updates Firestore and UI)
  const handleDeleteFlashcard = async (flashcardId) => {
    if (!flashcardId) {
      alert('Cannot delete: Flashcard has no ID.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this flashcard?')) return;
    try {
      await deleteDoc(doc(db, 'flashcards', flashcardId));
      await loadFlashcards(user.uid);
    } catch (err) {
      alert('Failed to delete flashcard: ' + err.message);
    }
  };

  // Delete wisdom hook (if from entry, deletes entry; if from flashcard, deletes flashcard)
  const handleDeleteWisdom = async (wisdom) => {
    if (!wisdom || !wisdom.id) {
      alert('Cannot delete: invalid wisdom entry');
      return;
    }
    if (wisdom.type === 'entry') {
      await handleDeleteEntry(wisdom.id);
    } else if (wisdom.type === 'flashcard') {
      await handleDeleteFlashcard(wisdom.id);
    }
  };

  if (!user) {
    return <AuthForm onLogin={setUser} />;
  }

  // --- Save entry and refresh from Firestore ---
  const handleSave = async () => {
    if (!entry.trim() && !hook.trim()) return;

    let newEntry = {
      text: entry.trim() || null,
      category,
      date: new Date().toISOString(),
      hook: hook.trim() || null,
      uid: user.uid,
      starred: starNext,
    };

    try {
      await addDoc(collection(db, 'entries'), newEntry);
      await loadEntries(user.uid);
    } catch (err) {
      alert('Error saving entry: ' + err.message);
    }

    // If there is a hook, always create a flashcard in Firestore
    if (hook.trim()) {
      const newCard = {
        prompt: hook.trim(),
        original: entry,
        interval: 1,
        repetition: 0,
        efactor: 2.5,
        dueDate: new Date().toISOString(),
        uid: user.uid,
      };
      try {
        await addDoc(collection(db, 'flashcards'), newCard);
        await loadFlashcards(user.uid);
      } catch (err) {
        alert('Error saving flashcard: ' + err.message);
      }
    }

    setEntry('');
    setHook('');
    setStarNext(false);
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  // Handle review response for flashcards
  const handleReviewResponse = async (quality) => {
    if (!reviewPrompt) return;
    const updatedCard = sm2Algorithm(reviewPrompt, quality);
    try {
      const ref = doc(db, 'flashcards', reviewPrompt.id);
      await updateDoc(ref, {
        interval: updatedCard.interval,
        repetition: updatedCard.repetition,
        efactor: updatedCard.efactor,
        dueDate: updatedCard.dueDate
      });
      await loadFlashcards(user.uid);
      setReviewPrompt(null);
    } catch (err) {
      alert('Failed to update flashcard: ' + err.message);
    }
  };

  const categories = [
    { key: 'relational', label: 'Relational', prompt: 'Did I spend quality time today with loved ones?' },
    { key: 'intellectual', label: 'Intellectual', prompt: 'What did I learn or explore that expanded my thinking?' },
    { key: 'spiritual', label: 'Spiritual', prompt: 'What gave me a sense of purpose or meaning today?' },
    { key: 'emotional', label: 'Emotional', prompt: 'Reflect and write about any emotions you experienced today. What are you thankful for?' },
    { key: 'nourishment', label: 'Nourishment', prompt: 'Did I give my body what it needed today (food, rest, movement, stress recovery)?' },
  ];

  // ---------- UI: Main Journal Page ----------
  if (showSection === 'journal') {
    return (
      <div style={{ maxWidth: '700px', margin: '2rem auto', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ textAlign: 'center', flex: 1 }}>RISEN Journal</h1>
          <button onClick={() => setShowSection('entries')} style={{ marginRight: 8 }}>
            Previous Entries
          </button>
          <button onClick={() => setShowSection('wisdom')} style={{ marginRight: 8 }}>
            Wisdom Bank
          </button>
          <button onClick={() => signOut(auth)} style={{ marginBottom: '1rem' }}>
            Sign Out
          </button>
        </div>
        {/* Category Buttons */}
        <div style={{ marginBottom: '1rem' }}>
          {categories.map(cat => (
            <button
              key={cat.key}
              onClick={() => setCategory(cat.key)}
              style={{
                marginRight: 6,
                fontWeight: category === cat.key ? 'bold' : undefined
              }}
            >
              {cat.label}
            </button>
          ))}
        </div>
        <p><i>{categories.find(c => c.key === category)?.prompt}</i></p>
        <textarea
          ref={textareaRef}
          value={entry}
          onChange={e => {
            setEntry(e.target.value);
            if (textareaRef.current) {
              textareaRef.current.style.height = "auto";
              textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
            }
          }}
          placeholder={`Write your reflection for ${category}...`}
          rows={4}
          style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem', resize: 'none', minHeight: 80 }}
          onInput={e => {
            if (textareaRef.current) {
              textareaRef.current.style.height = "auto";
              textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
            }
          }}
        />
        <input
          type="text"
          value={hook}
          onChange={e => setHook(e.target.value)}
          placeholder="Tag a key insight as a memory hook (optional)"
          style={{ width: '100%', padding: '0.5rem', marginBottom: '1rem' }}
        />
        <button
          aria-label={starNext ? "Unstar" : "Star"}
          onClick={() => setStarNext(s => !s)}
          style={{
            marginRight: 10,
            fontSize: 22,
            color: starNext ? '#FFD700' : '#ccc',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            verticalAlign: 'middle'
          }}
        >★</button>
        <button onClick={handleSave} style={{ padding: '0.5rem 1rem' }}>Save Entry / Hook</button>

        {/* Flashcard review modal */}
        {reviewPrompt && (
          <div style={{ marginTop: '2rem', padding: '1rem', backgroundColor: '#fff8dc' }}>
            <h2>Flashcard Review</h2>
            <p><i>"{reviewPrompt.prompt}"</i></p>
            <p>How well did you recall this?</p>
            {[0, 1, 2, 3, 4, 5].map(q => (
              <button
                key={q}
                onClick={() => handleReviewResponse(q)}
                style={{ margin: '0.25rem' }}
              >{q}</button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- UI: Previous Entries Page ----------
  if (showSection === 'entries') {
    return (
      <div style={{ maxWidth: '700px', margin: '2rem auto', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={() => setShowSection('journal')} style={{ marginRight: 8 }}>
            ← Back to Journal
          </button>
          <h2 style={{ flex: 1, textAlign: 'center' }}>Previous Entries</h2>
          <button onClick={() => setShowSection('wisdom')} style={{ marginRight: 8 }}>
            Wisdom Bank
          </button>
          <button onClick={() => signOut(auth)} style={{ marginBottom: '1rem' }}>
            Sign Out
          </button>
        </div>
        <div style={{ marginBottom: 12 }}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search entries..."
            style={{ width: '65%', padding: '0.5rem', marginRight: 8 }}
          />
          <button
            onClick={() => setEntriesTab('all')}
            style={{ fontWeight: entriesTab === 'all' ? 'bold' : undefined, marginRight: 4 }}
          >
            All
          </button>
          <button
            onClick={() => setEntriesTab('starred')}
            style={{ fontWeight: entriesTab === 'starred' ? 'bold' : undefined }}
          >
            ⭐ Starred
          </button>
        </div>
        {Object.keys(groupedEntries).length === 0 && <p>No entries found.</p>}
        {Object.entries(groupedEntries).map(([date, dayEntries]) => (
          <div key={date} style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ marginBottom: 5 }}>{date}</h4>
            {dayEntries.filter(e => e.id).map(e => (
              <div key={e.id} style={{
                border: e.starred ? '2px solid gold' : '1px solid #ccc',
                padding: '1rem',
                marginBottom: '0.5rem',
                background: e.starred ? '#fffbe6' : '#fafcff'
              }}>
                {/* DELETE BUTTON */}
                <button
                  onClick={() => handleDeleteEntry(e.id)}
                  style={{
                    color: 'crimson',
                    float: 'right',
                    fontSize: 18,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    marginRight: 8
                  }}
                  aria-label="Delete"
                >🗑️</button>
                {/* STAR BUTTON */}
                <button
                  onClick={() => toggleStar(e)}
                  style={{
                    color: e.starred ? '#FFD700' : '#ccc',
                    float: 'right',
                    fontSize: 20,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer'
                  }}
                  aria-label={e.starred ? 'Unstar' : 'Star'}
                >★</button>
                <p>
                  <small>
                    {category && categories.find(c => c.key === e.category)?.label}
                  </small>
                </p>
                <p style={{
                  whiteSpace: 'pre-line',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere'
                }}>
                  {e.text}
                </p>
                {e.hook && <p><i>Hook: "{e.hook}"</i></p>}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  // ---------- UI: Wisdom Bank Page ----------
  if (showSection === 'wisdom') {
    return (
      <div style={{ maxWidth: '700px', margin: '2rem auto', fontFamily: 'Arial, sans-serif' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={() => setShowSection('journal')} style={{ marginRight: 8 }}>
            ← Back to Journal
          </button>
          <h2 style={{ flex: 1, textAlign: 'center' }}>Wisdom Bank</h2>
          <button onClick={() => setShowSection('entries')} style={{ marginRight: 8 }}>
            Previous Entries
          </button>
          <button onClick={() => signOut(auth)} style={{ marginBottom: '1rem' }}>
            Sign Out
          </button>
        </div>
        {Object.keys(groupedWisdom).length === 0 && <p>No wisdom hooks found.</p>}
        {Object.entries(groupedWisdom).map(([date, hooks]) => (
          <div key={date} style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ marginBottom: 5 }}>{date}</h4>
            {hooks.filter(w => w.id).map((w, idx) => (
              <div key={w.id || idx} style={{
                border: '1px solid #87cefa',
                padding: '1rem',
                marginBottom: '0.5rem',
                background: '#f3faff',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span style={{ flex: 1 }}><i>"{w.hook}"</i></span>
                <button
                  onClick={() => handleDeleteWisdom(w)}
                  style={{
                    color: 'crimson',
                    fontSize: 18,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    marginLeft: 10
                  }}
                  aria-label="Delete"
                >🗑️</button>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }
}
